// This is a fork from VueUse's "useUrlSearchParams"
// It
// 1. revokes some of the encodings done by the native URLSearchParams class, just to look a bit prettier
// 2. writes back to the Vue Router instead of the window.history directly

import { type ConfigurableWindow, defaultWindow } from '@vueuse/core'

export type UrlParams = Record<string, string[] | string>

export interface UseRouterQueryParamsOptions<T> extends ConfigurableWindow {
  /**
   * @default true
   */
  removeNullishValues?: boolean

  /**
   * @default false
   */
  removeFalsyValues?: boolean

  /**
   * @default {}
   */
  initialValue?: T

  /**
   * Write back to `window.history` automatically
   *
   * @default true
   */
  write?: boolean
}

/**
 * Reactive URLSearchParams
 *
 * @see https://vueuse.org/useUrlSearchParams
 * @param mode
 * @param options
 */
export function useRouterQueryParams<T extends Record<string, any> = UrlParams>(
  mode: 'history' | 'hash' | 'hash-params' = 'history',
  options: UseRouterQueryParamsOptions<T> = {},
): T {
  const {
    initialValue = {},
    removeNullishValues = true,
    removeFalsyValues = false,
    write: enableWrite = true,
    window = defaultWindow!,
  } = options

  if (!window) return reactive(initialValue) as T

  const route = useRoute()
  const router = useRouter()

  const state: Record<string, any> = reactive({})

  function getRawParams() {
    if (mode === 'history') {
      return window.location.search || ''
    } else if (mode === 'hash') {
      const hash = window.location.hash || ''
      const index = hash.indexOf('?')
      return index > 0 ? hash.slice(index) : ''
    } else {
      return (window.location.hash || '').replace(/^#/, '')
    }
  }

  function read() {
    return new URLSearchParams(getRawParams())
  }

  function updateState(params: URLSearchParams) {
    const unusedKeys = new Set(Object.keys(state))
    for (const key of params.keys()) {
      const paramsForKey = params.getAll(key)
      state[key] = paramsForKey.length > 1 ? paramsForKey : params.get(key) || ''
      unusedKeys.delete(key)
    }
    Array.from(unusedKeys).forEach(key => delete state[key])
  }

  const { pause, resume } = pausableWatch(
    state,
    () => {
      const params = new URLSearchParams('')
      Object.keys(state).forEach(key => {
        const mapEntry = state[key]
        if (Array.isArray(mapEntry)) mapEntry.forEach(value => params.append(key, value))
        else if (removeNullishValues && mapEntry == null) params.delete(key)
        else if (removeFalsyValues && !mapEntry) params.delete(key)
        else params.set(key, mapEntry)
      })
      write(params)
    },
    { deep: true },
  )

  function write(params: URLSearchParams, shouldUpdate?: boolean) {
    if (JSON.stringify(Object.fromEntries(params)) === JSON.stringify(route.params)) return

    pause()

    if (shouldUpdate) updateState(params)

    router.replace({
      path: route.path,
      query: Object.fromEntries(params),
    })

    resume()
  }

  function onChanged() {
    if (!enableWrite) return

    write(read(), true)
  }

  useEventListener(window, 'popstate', onChanged, false)
  if (mode !== 'history') useEventListener(window, 'hashchange', onChanged, false)

  const initial = read()
  if (initial.keys().next().value) updateState(initial)
  else Object.assign(state, initialValue)

  router.afterEach(() => {
    const initial = read()
    if (initial.keys().next().value) updateState(initial)
  })

  return state as T
}
